import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hexagon, ShieldCheck } from "lucide-react";

export default function Setup() {
  const { setUser } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/setup-status").then((r) => { if (!r.data.needsSetup) nav("/login"); }).catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/setup", f);
      setUser(data);
      nav("/");
    } catch (e2) { setErr(apiErr(e2)); } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-5 py-10 sm:px-6 sm:py-12">
      <div className="w-full max-w-md border border-zinc-200 bg-white p-6 sm:p-8">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground"><Hexagon size={20} strokeWidth={2.5} /></div>
          <div className="leading-none">
            <div className="font-display text-base font-black uppercase tracking-tight text-zinc-950">Scalio Nexus</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t("brand.subtitle")}</div>
          </div>
        </div>
        <h1 className="font-display text-2xl font-black uppercase tracking-tighter text-zinc-950">{t("setup.title")}</h1>
        <p className="mt-2 text-sm text-zinc-500">{t("setup.subtitle")}</p>
        <form onSubmit={submit} className="mt-7 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("setup.name")}</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="setup-name" className="rounded-sm border-zinc-300" required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("login.email")}</Label>
            <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="setup-email" className="rounded-sm border-zinc-300" required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("login.password")}</Label>
            <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="setup-password" className="rounded-sm border-zinc-300" required />
          </div>
          {err && <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="setup-error">{err}</div>}
          <Button type="submit" disabled={loading} data-testid="setup-submit" className="h-11 w-full rounded-sm bg-primary text-sm font-semibold uppercase tracking-wide hover:bg-[#002480]">
            <ShieldCheck size={16} className="mr-2" /> {loading ? t("setup.creating") : t("setup.create")}
          </Button>
        </form>
      </div>
    </div>
  );
}
